import { useState } from "react";
import { Alert, Button, Divider, Stack } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../../components/primitives/layout/PageContainer";
import { PageHeader } from "../../components/primitives/layout/PageHeader";
import { Section } from "../../components/primitives/layout/Section";
import { gaps } from "../../components/primitives/spacing";
import { Meta } from "../../components/primitives/data-display/Meta";
import { Note } from "../../components/primitives/data-display/Note";
import { ClaimDisplay } from "../../components/domain/data-display/ClaimDisplay";
import { LoadingState } from "../../components/primitives/feedback/LoadingState";
import { EmptyState } from "../../components/primitives/feedback/EmptyState";
import { ErrorState } from "../../components/domain/feedback/ErrorState";
import { SourceCard } from "../../components/domain/data-display/SourceCard";
import { FindingsCard } from "../../components/domain/data-display/FindingsCard";
import { ExplanationCard } from "../../components/domain/data-display/ExplanationCard";
import { ExpertInsightCard } from "../../components/domain/data-display/ExpertInsightCard";
import { FeedbackBar } from "../../components/domain/feedback/FeedbackBar";
import { ShareButton } from "../../components/domain/inputs/ShareButton";
import NoCoverage, { CoverageRequestForm } from "../NoCoverage";
import { submitCoverageRequest } from "../../lib/claimsApi";
import { isGapVerdict } from "../../types/claims";
import { useResult } from "./useResult";
import { ROUTES, feedbackPath } from "../../route_paths";

const INITIAL_SHOWN = 3;

/**
 * The result page. Hydrates from React Router state first.
 * Shareable via URL — recipients without a cached result see a "not found" state.
 */
export default function Result() {
  const {
    result,
    isLoading,
    apiError,
    claimId,
    userEventVerificationId,
    sortedEvidence,
    fallacy,
    isFallacyLoading,
    explanation,
    isExplanationLoading,
    groupedInsights,
    evidenceMetadata,
    hasNoCoverage,
  } = useResult();
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  if (isLoading) {
    return (
      <PageContainer center>
        <LoadingState />
      </PageContainer>
    );
  }

  if (apiError) {
    return (
      <PageContainer center>
        <ErrorState
          code={apiError.code}
          action={
            <Button variant="contained" component={Link} to={ROUTES.landing.path()}>
              {t("common.startOver")}
            </Button>
          }
        />
      </PageContainer>
    );
  }

  if (!result) {
    return (
      <PageContainer center>
        <EmptyState
          message={t("result.notFound")}
          action={
            <Button variant="contained" component={Link} to={ROUTES.landing.path()}>
              {t("common.startOver")}
            </Button>
          }
        />
      </PageContainer>
    );
  }

  const { claim, claim_verdict } = result;
  if (hasNoCoverage && userEventVerificationId) {
    return (
      <NoCoverage
        reason="health"
        claimText={result.raw_text ?? claim.canonical}
        submitRequest={(email) => submitCoverageRequest(userEventVerificationId, email)}
      />
    );
  }
  const displayedEvidence = showAll ? sortedEvidence : sortedEvidence.slice(0, INITIAL_SHOWN);
  const hasMore = !showAll && sortedEvidence.length > INITIAL_SHOWN;

  return (
    <PageContainer>
      <PageHeader
        align="center"
        title="Your results are in!"
        subtitle="We have analysed and verified your claims. You can see the results below."
      />

      {result.stale_since && <Alert severity="warning">{t("result.staleWarning")}</Alert>}

      <Section title={t("result.claimChecked")}>
        <ClaimDisplay claim={result.raw_text ?? claim.canonical} />
      </Section>

      <Section title="Our findings">
        <FindingsCard
          verdict={claim_verdict.verdict}
          signal={claim_verdict.signal}
          evidenceMass={claim_verdict.evidence_mass}
          evidence={sortedEvidence}
          sourcesScanned={claim_verdict.sources_scanned}
          fallacyTags={fallacy?.tags ?? []}
          fallacyDefinitions={fallacy?.definitions ?? {}}
          isFallacyLoading={isFallacyLoading}
        />
      </Section>

      {isGapVerdict(claim_verdict.verdict) && userEventVerificationId && (
        <Section title={t("result.insufficientRequestTitle")}>
          <CoverageRequestForm
            prompt={t("result.insufficientRequestPrompt")}
            submitRequest={(email) => submitCoverageRequest(userEventVerificationId, email)}
          />
        </Section>
      )}

      {(isExplanationLoading || explanation) && (
        <Section title={t("explanation.sectionLabel")}>
          <ExplanationCard
            text={explanation?.text ?? null}
            isLoading={isExplanationLoading}
            evidenceCount={sortedEvidence.length}
          />
        </Section>
      )}

      {groupedInsights.length > 0 && (
        <Section title={t("expertInsight.section")}>
          <Stack gap={gaps.base}>
            {groupedInsights.map((group) => (
              <ExpertInsightCard
                key={group.authorId}
                expertName={group.expertName}
                expertCredentials={group.expertCredentials}
                expertBio={group.expertBio}
                expertAvatarUrl={group.expertAvatarUrl}
                insights={group.insights}
              />
            ))}
          </Stack>
        </Section>
      )}

      <Section
        title={t("result.evidence")}
        action={<Meta>{t("result.papersCount", { count: sortedEvidence.length })}</Meta>}
      >
        {sortedEvidence.length === 0 && <Note>{t("result.noSources")}</Note>}

        {displayedEvidence.map((ev, i) => (
          <SourceCard
            key={ev.source_id}
            evidence={ev}
            anchorId={`source-${i + 1}`}
            publicationTypes={evidenceMetadata.get(ev.source_id)?.publication_types}
          />
        ))}

        {hasMore && (
          <Button variant="outlined" fullWidth onClick={() => setShowAll(true)} sx={{ mt: 0.5 }}>
            {t("result.viewAll", { count: sortedEvidence.length })}
          </Button>
        )}
      </Section>

      <Divider />

      <FeedbackBar claimId={claimId ?? ""} />

      {userEventVerificationId && (
        <Stack alignItems="center">
          <Button
            component={Link}
            to={feedbackPath({
              verification: userEventVerificationId,
              from: ROUTES.verify.path({ userEventVerificationId }),
            })}
            variant="text"
            size="small"
          >
            {t("result.reportProblem")}
          </Button>
        </Stack>
      )}

      <Stack
        direction="row"
        gap={gaps.base}
        sx={{ justifyContent: "center", flexWrap: "wrap", pb: 2 }}
      >
        <ShareButton claimId={claimId} />
        <Button
          variant="contained"
          component={Link}
          to={ROUTES.landing.path()}
          startIcon={<AddCircleOutlineIcon />}
        >
          {t("common.verifyAnotherShort")}
        </Button>
      </Stack>

      <Note variant="caption" sx={{ textAlign: "center", pb: 2 }}>
        {t("result.disclaimer")}
      </Note>
    </PageContainer>
  );
}
